import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Table,
  Tag,
  Space,
  Button,
  Tabs,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  InputNumber,
  Switch,
  Statistic,
  Tooltip,
  Popconfirm,
  Empty,
  Spin,
  message,
  theme,
  Divider,
  Radio,
  Alert,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SettingOutlined,
  LinkOutlined,
  BankOutlined,
  TeamOutlined,
  UserOutlined,
  DesktopOutlined,
  AppstoreOutlined,
  ReloadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";
import PageLayout from "@components/pageLayout/PageLayout.react";
import { authenticatedFetch } from "@/libs/api";
import { makePagination } from "@components/common/TablePagination";
import FormulaBuilder from "./FormulaBuilder.react";
import dayjs from "dayjs";

const { Title, Text } = Typography;
const { TextArea } = Input;

// Rule types
const RULE_TYPES = [
  { value: "GROUP_REVENUE", label: "Group Revenue", description: "Revenue rules for control groups", color: "blue" },
  { value: "SYSTEM_ALLOCATION", label: "System Allocation", description: "Revenue allocation to systems", color: "green" },
  { value: "COMPANY_REVENUE", label: "Company Revenue", description: "Revenue rules for companies", color: "purple" },
  { value: "CUSTOMER_PRICING", label: "Customer Pricing", description: "Pricing rules for customers", color: "orange" },
];

// Binding types (view-group removed - not needed for settlement).
// The "company" key is a historical name; under the hood it now binds an
// algorithm (settlement rule) to a vg_share ROW (VG + company + share%),
// not to a company entity. See settlementController.js partner share branch.
const BINDING_TYPES = [
  { key: "control-group", label: "Control Group", icon: <TeamOutlined />, description: "Bind rules to control groups" },
  { key: "system", label: "System", icon: <DesktopOutlined />, description: "Bind allocation rules to systems" },
  { key: "company", label: "Partner Share", icon: <BankOutlined />, description: "Bind algorithm to (VG + Partner) share row" },
  { key: "customer", label: "Customer", icon: <UserOutlined />, description: "Bind pricing rules to customers" },
];

// Data source configuration with allowed tables and columns
const DATA_SOURCE_CONFIG = {
  MYSQL: {
    label: "PostgreSQL",
    tables: {
      np_system: {
        label: "System",
        columns: [
          { value: "inverter_kw", label: "Inverter Capacity (kW)" },
          { value: "battery_kwh", label: "Battery Capacity (kWh)" },
          { value: "pv_kw", label: "PV Capacity (kW)" },
        ],
      },
      np_company: {
        label: "Company",
        columns: [
          { value: "id", label: "Company ID" },
          { value: "name", label: "Company Name" },
        ],
      },
      np_vpp_plan: {
        label: "VPP Plan",
        columns: [
          { value: "revenue_share_pct", label: "Revenue Share %" },
          { value: "min_daily_payment", label: "Min Daily Payment" },
        ],
      },
      np_control_group_daily_snapshot: {
        label: "Control Group Daily Snapshot (PostgreSQL)",
        columns: [
          { value: "total_credit", label: "Total Credit ($)" },
          { value: "total_debit", label: "Total Debit ($)" },
          { value: "system_count", label: "System Count" },
          { value: "online_count", label: "Online Count" },
          { value: "activated_count", label: "Activated Count" },
          { value: "online_pct", label: "Online Percentage" },
          { value: "total_inverter_kw", label: "Total Inverter (kW)" },
          { value: "total_battery_kwh", label: "Total Battery (kWh)" },
          { value: "total_headroom_kw", label: "Total Headroom (kW)" },
          { value: "total_footroom_kw", label: "Total Footroom (kW)" },
          { value: "total_epv", label: "Total PV Energy (kWh)" },
          { value: "total_eload", label: "Total Load Energy (kWh)" },
          { value: "total_esell", label: "Total Grid Export (kWh)" },
          { value: "total_ebuy", label: "Total Grid Import (kWh)" },
          { value: "total_echarge", label: "Total Battery Charge (kWh)" },
          { value: "total_edischarge", label: "Total Battery Discharge (kWh)" },
          { value: "avg_soc", label: "Average SOC (%)" },
          { value: "dispatch_count", label: "Dispatch Count" },
          { value: "dispatch_compliance_rate", label: "Dispatch Compliance Rate" },
          { value: "performance_score", label: "Performance Score" },
        ],
      },
      np_system_daily_snapshot: {
        label: "System Daily Snapshot (PostgreSQL)",
        columns: [
          { value: "total_credit", label: "Total Credit ($)" },
          { value: "total_debit", label: "Total Debit ($)" },
          { value: "online_pct", label: "Online Percentage" },
          { value: "online_interval_count", label: "Online Interval Count" },
          { value: "inverter_kw", label: "Inverter Capacity (kW)" },
          { value: "battery_kwh", label: "Battery Capacity (kWh)" },
          { value: "headroom_kw", label: "Headroom (kW)" },
          { value: "footroom_kw", label: "Footroom (kW)" },
          { value: "total_epv", label: "Total PV Energy (kWh)" },
          { value: "total_eload", label: "Total Load Energy (kWh)" },
          { value: "total_esell", label: "Total Grid Export (kWh)" },
          { value: "total_ebuy", label: "Total Grid Import (kWh)" },
          { value: "total_echarge", label: "Total Battery Charge (kWh)" },
          { value: "total_edischarge", label: "Total Battery Discharge (kWh)" },
          { value: "avg_soc", label: "Average SOC (%)" },
          { value: "dispatch_count", label: "Dispatch Count" },
          { value: "dispatch_compliance_rate", label: "Dispatch Compliance Rate" },
          { value: "performance_score", label: "Performance Score" },
        ],
      },
    },
  },
  CASSANDRA: {
    label: "Cassandra",
    tables: {
      powow_np_control_group_daily_snapshot: {
        label: "Control Group Daily Snapshot",
        columns: [
          { value: "system_count", label: "System Count" },
          { value: "online_count", label: "Online Count" },
          { value: "total_inverter_kw", label: "Total Inverter (kW)" },
          { value: "total_battery_kwh", label: "Total Battery (kWh)" },
          { value: "total_pv_kwh", label: "Total PV (kWh)" },
          { value: "total_battery_discharge_kwh", label: "Total Battery Discharge (kWh)" },
          { value: "total_vpp_discharge_kwh", label: "Total VPP Discharge (kWh)" },
          { value: "today_pv_kwh", label: "Today PV [288]", isArray: true },
          { value: "today_battery_discharge_kwh", label: "Today Battery Discharge [288]", isArray: true },
          { value: "today_vpp_discharge_kwh", label: "Today VPP Discharge [288]", isArray: true },
          { value: "today_soc_avg_pct", label: "Today SOC Avg [288]", isArray: true },
          { value: "today_online_count", label: "Today Online Count [288]", isArray: true },
          { value: "today_revenue", label: "Today Revenue" },
          { value: "total_revenue", label: "Total Revenue" },
        ],
      },
      powow_np_system_daily_snapshot: {
        label: "System Daily Snapshot",
        columns: [
          { value: "online_intervals", label: "Online Intervals" },
          { value: "total_intervals", label: "Total Intervals" },
          { value: "online_pct", label: "Online Percentage" },
          { value: "total_pv_kwh", label: "Total PV (kWh)" },
          { value: "total_load_kwh", label: "Total Load (kWh)" },
          { value: "total_battery_discharge_kwh", label: "Total Battery Discharge (kWh)" },
          { value: "total_battery_charge_kwh", label: "Total Battery Charge (kWh)" },
          { value: "total_vpp_discharge_kwh", label: "Total VPP Discharge (kWh)" },
          { value: "total_grid_export_kwh", label: "Total Grid Export (kWh)" },
          { value: "total_grid_import_kwh", label: "Total Grid Import (kWh)" },
          { value: "today_pv_kw", label: "Today PV [288]", isArray: true },
          { value: "today_load_kw", label: "Today Load [288]", isArray: true },
          { value: "today_battery_kw", label: "Today Battery [288]", isArray: true },
          { value: "today_soc_pct", label: "Today SOC [288]", isArray: true },
          { value: "today_grid_kw", label: "Today Grid [288]", isArray: true },
          { value: "today_revenue", label: "Today Revenue" },
        ],
      },
      powow_np_aemo_dispatchload: {
        label: "AEMO Dispatch Load (Raw)",
        columns: [
          { value: "totaldemand", label: "Total Demand" },
          { value: "availablegeneration", label: "Available Generation" },
          { value: "dispatchablegeneration", label: "Dispatchable Generation" },
          { value: "initialsupply", label: "Initial Supply" },
          { value: "clearedsupply", label: "Cleared Supply" },
          { value: "rrp", label: "RRP ($/MWh)" },
        ],
      },
      powow_np_aemo_dispatchload_aggregation_day: {
        label: "AEMO Dispatch Load (Daily Aggregation)",
        columns: [
          // 288-point arrays
          { value: "totaldemand_288", label: "Total Demand [288]", isArray: true },
          { value: "availablegeneration_288", label: "Available Generation [288]", isArray: true },
          { value: "dispatchablegeneration_288", label: "Dispatchable Generation [288]", isArray: true },
          { value: "initialsupply_288", label: "Initial Supply [288]", isArray: true },
          { value: "clearedsupply_288", label: "Cleared Supply [288]", isArray: true },
          { value: "rrp_288", label: "RRP [288]", isArray: true },
          { value: "raise6secrrp_288", label: "Raise 6sec RRP [288]", isArray: true },
          { value: "raise60secrrp_288", label: "Raise 60sec RRP [288]", isArray: true },
          { value: "raise5minrrp_288", label: "Raise 5min RRP [288]", isArray: true },
          { value: "raiseregrrp_288", label: "Raise Reg RRP [288]", isArray: true },
          { value: "lower6secrrp_288", label: "Lower 6sec RRP [288]", isArray: true },
          { value: "lower60secrrp_288", label: "Lower 60sec RRP [288]", isArray: true },
          { value: "lower5minrrp_288", label: "Lower 5min RRP [288]", isArray: true },
          { value: "lowerregrrp_288", label: "Lower Reg RRP [288]", isArray: true },
          { value: "raise1secrrp_288", label: "Raise 1sec RRP [288]", isArray: true },
          { value: "lower1secrrp_288", label: "Lower 1sec RRP [288]", isArray: true },
          // Daily aggregates
          { value: "totaldemand_avg", label: "Total Demand Avg" },
          { value: "totaldemand_max", label: "Total Demand Max" },
          { value: "totaldemand_min", label: "Total Demand Min" },
          { value: "availablegeneration_avg", label: "Available Generation Avg" },
          { value: "dispatchablegeneration_avg", label: "Dispatchable Generation Avg" },
          { value: "initialsupply_total", label: "Initial Supply Total" },
          { value: "clearedsupply_total", label: "Cleared Supply Total" },
          { value: "rrp_avg", label: "RRP Avg" },
          { value: "rrp_max", label: "RRP Max" },
          { value: "rrp_min", label: "RRP Min" },
          // FCAS RRP daily averages
          { value: "raise6secrrp_avg", label: "Raise 6sec RRP Avg" },
          { value: "raise60secrrp_avg", label: "Raise 60sec RRP Avg" },
          { value: "raise5minrrp_avg", label: "Raise 5min RRP Avg" },
          { value: "raiseregrrp_avg", label: "Raise Reg RRP Avg" },
          { value: "lower6secrrp_avg", label: "Lower 6sec RRP Avg" },
          { value: "lower60secrrp_avg", label: "Lower 60sec RRP Avg" },
          { value: "lower5minrrp_avg", label: "Lower 5min RRP Avg" },
          { value: "lowerregrrp_avg", label: "Lower Reg RRP Avg" },
          { value: "raise1secrrp_avg", label: "Raise 1sec RRP Avg" },
          { value: "lower1secrrp_avg", label: "Lower 1sec RRP Avg" },
          { value: "interval_count", label: "Interval Count" },
        ],
      },
      powow_np_aemo_set_fcas_payment_aggregation_day: {
        label: "AEMO FCAS Payment (Daily Aggregation)",
        columns: [
          // 288-point arrays for each market
          { value: "lower1sec_payment_288", label: "Lower 1sec Payment [288]", isArray: true },
          { value: "lower5min_payment_288", label: "Lower 5min Payment [288]", isArray: true },
          { value: "lower60sec_payment_288", label: "Lower 60sec Payment [288]", isArray: true },
          { value: "lower6sec_payment_288", label: "Lower 6sec Payment [288]", isArray: true },
          { value: "lowerreg_payment_288", label: "Lower Reg Payment [288]", isArray: true },
          { value: "raise1sec_payment_288", label: "Raise 1sec Payment [288]", isArray: true },
          { value: "raise5min_payment_288", label: "Raise 5min Payment [288]", isArray: true },
          { value: "raise60sec_payment_288", label: "Raise 60sec Payment [288]", isArray: true },
          { value: "raise6sec_payment_288", label: "Raise 6sec Payment [288]", isArray: true },
          { value: "raisereg_payment_288", label: "Raise Reg Payment [288]", isArray: true },
          // Aggregated 288-point arrays
          { value: "lower_payment_288", label: "Lower Total Payment [288]", isArray: true },
          { value: "raise_payment_288", label: "Raise Total Payment [288]", isArray: true },
          { value: "fcas_payment_288", label: "FCAS Total Payment [288]", isArray: true },
          // Daily totals per market
          { value: "lower1sec_payment_today", label: "Lower 1sec Payment Today" },
          { value: "lower5min_payment_today", label: "Lower 5min Payment Today" },
          { value: "lower60sec_payment_today", label: "Lower 60sec Payment Today" },
          { value: "lower6sec_payment_today", label: "Lower 6sec Payment Today" },
          { value: "lowerreg_payment_today", label: "Lower Reg Payment Today" },
          { value: "raise1sec_payment_today", label: "Raise 1sec Payment Today" },
          { value: "raise5min_payment_today", label: "Raise 5min Payment Today" },
          { value: "raise60sec_payment_today", label: "Raise 60sec Payment Today" },
          { value: "raise6sec_payment_today", label: "Raise 6sec Payment Today" },
          { value: "raisereg_payment_today", label: "Raise Reg Payment Today" },
          // Summary daily totals
          { value: "lower_payment_today", label: "Lower Total Payment Today" },
          { value: "raise_payment_today", label: "Raise Total Payment Today" },
          { value: "fcas_payment_today", label: "FCAS Total Payment Today" },
        ],
      },
    },
  },
  PLATFORM_STATISTICS: {
    label: "Platform Statistics",
    tables: {
      np_system: {
        label: "System (Platform Stats)",
        columns: [
          { value: "platform_onboard_today_count", label: "Today Onboarded Count" },
          { value: "platform_onboard_today_mw", label: "Today Onboarded MW" },
          { value: "platform_onboard_today_mwh", label: "Today Onboarded MWh" },
          { value: "platform_offboard_today_count", label: "Today Offboarded Count" },
          { value: "platform_offboard_today_mw", label: "Today Offboarded MW" },
          { value: "platform_offboard_today_mwh", label: "Today Offboarded MWh" },
          { value: "platform_retain_3m_count", label: "Retained 3+ Months Count" },
          { value: "platform_retain_3m_mw", label: "Retained 3+ Months MW" },
          { value: "platform_retain_3m_mwh", label: "Retained 3+ Months MWh" },
          { value: "platform_retain_6m_count", label: "Retained 6+ Months Count" },
          { value: "platform_retain_6m_mw", label: "Retained 6+ Months MW" },
          { value: "platform_retain_6m_mwh", label: "Retained 6+ Months MWh" },
          { value: "platform_retain_9m_count", label: "Retained 9+ Months Count" },
          { value: "platform_retain_9m_mw", label: "Retained 9+ Months MW" },
          { value: "platform_retain_9m_mwh", label: "Retained 9+ Months MWh" },
          { value: "platform_retain_12m_count", label: "Retained 12+ Months Count" },
          { value: "platform_retain_12m_mw", label: "Retained 12+ Months MW" },
          { value: "platform_retain_12m_mwh", label: "Retained 12+ Months MWh" },
        ],
      },
    },
  },
};

export default function SettlementRuleManagement() {
  const { token } = theme.useToken();
  const [ruleForm] = Form.useForm();
  const [bindingForm] = Form.useForm();
  const [activeTab, setActiveTab] = useState("rules");
  const [loading, setLoading] = useState(true);
  const [rules, setRules] = useState([]);
  const [bindings, setBindings] = useState([]);
  const [summary, setSummary] = useState({});
  const [ruleModalVisible, setRuleModalVisible] = useState(false);
  const [bindingModalVisible, setBindingModalVisible] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editingBinding, setEditingBinding] = useState(null);
  const [selectedBindingType, setSelectedBindingType] = useState("control-group");
  const [saveLoading, setSaveLoading] = useState(false);
  const [ruleFilter, setRuleFilter] = useState("all");
  const [selectedRuleType, setSelectedRuleType] = useState("GROUP_REVENUE");
  const [formulaData, setFormulaData] = useState(null);
  const [rulePageSize, setRulePageSize] = useState(10);
  const [bindingPageSize, setBindingPageSize] = useState(20);
  const [variablePageSize, setVariablePageSize] = useState(10);

  // Customer batch binding mode
  const [customerBindingMode, setCustomerBindingMode] = useState("single"); // 'single' or 'batch'
  const [batchFilterType, setBatchFilterType] = useState("control-group"); // 'control-group' or 'company'
  const [batchFilterValue, setBatchFilterValue] = useState(null);

  // Entities for binding (fetched from APIs)
  const [controlGroups, setControlGroups] = useState([]);
  const [viewGroups, setViewGroups] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  // For "company" (Partner Share) binding: list of vg_share rows that can have a rule attached
  const [vgShares, setVgShares] = useState([]);

  // Variable Management state
  const [variables, setVariables] = useState([]);
  const [variableModalVisible, setVariableModalVisible] = useState(false);
  const [editingVariable, setEditingVariable] = useState(null);
  const [variableForm] = Form.useForm();
  const [variableLoading, setVariableLoading] = useState(false);
  const [variableFilter, setVariableFilter] = useState("all");

  // Batch delete bindings
  const [selectedBindingKeys, setSelectedBindingKeys] = useState([]);
  const [batchDeleteLoading, setBatchDeleteLoading] = useState(false);

  // Fetch rules
  const fetchRules = useCallback(async () => {
    try {
      const response = await authenticatedFetch("/api/settlements/rules");
      const data = await response.json();
      if (data.success) {
        setRules(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch rules:", error);
    }
  }, []);

  // Fetch bindings for selected type
  const fetchBindings = useCallback(async (type) => {
    try {
      const response = await authenticatedFetch(`/api/settlements/bindings/${type}`);
      const data = await response.json();
      if (data.success) {
        setBindings(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch bindings:", error);
    }
  }, []);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await authenticatedFetch("/api/settlements/bindings-summary");
      const data = await response.json();
      if (data.success) {
        setSummary(data.data || {});
      }
    } catch (error) {
      console.error("Failed to fetch summary:", error);
    }
  }, []);

  // Fetch entities for binding dropdowns
  const fetchEntities = useCallback(async () => {
    try {
      const [groupsRes, viewGroupsRes, companiesRes, customersRes, vgSharesRes] = await Promise.all([
        authenticatedFetch("/api/control-groups"),
        authenticatedFetch("/api/groups"),
        authenticatedFetch("/api/companies"),
        authenticatedFetch("/api/customer"),
        // Partner share rows — used as "entity" options for the company binding type
        authenticatedFetch("/api/vg-shares?include_inactive=0"),
      ]);

      const groupsData = await groupsRes.json();
      const viewGroupsData = await viewGroupsRes.json();
      const companiesData = await companiesRes.json();
      const customersData = await customersRes.json();
      const vgSharesData = await vgSharesRes.json();

      if (groupsData.success) setControlGroups(groupsData.data || []);
      if (viewGroupsData.success) setViewGroups(viewGroupsData.data || []);
      if (companiesData.success) setCompanies(companiesData.data || companiesData.companies || []);
      if (customersData.success) setCustomers(customersData.data || customersData.customers || []);
      if (vgSharesData.success) setVgShares(vgSharesData.data || []);
    } catch (error) {
      console.error("Failed to fetch entities:", error);
    }
  }, []);

  // Fetch variables
  const fetchVariables = useCallback(async () => {
    try {
      setVariableLoading(true);
      const response = await authenticatedFetch("/api/settlement-variables");
      const data = await response.json();
      if (data.success) {
        setVariables(data.data || []);
      }
    } catch (error) {
      console.error("Failed to fetch variables:", error);
    } finally {
      setVariableLoading(false);
    }
  }, []);

  // Create/Update variable
  const handleSaveVariable = async (values) => {
    setSaveLoading(true);
    try {
      const isEdit = !!editingVariable;

      const payload = {
        variableName: values.variableName,
        displayName: values.displayName,
        description: values.description,
        ruleType: values.ruleType,
        dataSource: values.dataSource,
        tableName: values.tableName,
        columnName: values.columnName,
        dataType: values.dataType,
        isArray: values.isArray || false,
      };

      const url = isEdit
        ? `/api/settlement-variables/${editingVariable.variable_id}`
        : "/api/settlement-variables";
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        message.success(isEdit ? "Variable updated" : "Variable created");
        setVariableModalVisible(false);
        setEditingVariable(null);
        variableForm.resetFields();
        fetchVariables();
      } else {
        message.error(data.message || "Failed to save variable");
      }
    } catch (error) {
      message.error("Failed to save variable");
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete variable
  const handleDeleteVariable = async (variableId) => {
    // Check if variable is used in any rule
    const variableInfo = variables.find(v => v.variable_id === variableId);
    const usedInRules = rules.filter(rule => {
      if (!rule.formula_json) return false;
      try {
        const formula = typeof rule.formula_json === 'string' ? JSON.parse(rule.formula_json) : rule.formula_json;
        if (!formula.variables) return false;
        // Check if any variable in the formula references this variable_id
        return Object.values(formula.variables).some(v => v.variable_id === variableId);
      } catch {
        return false;
      }
    });

    if (usedInRules.length > 0) {
      const ruleNames = usedInRules.map(r => r.rule_name).join(', ');
      message.error(`Cannot delete: Variable "${variableInfo?.display_name || variableId}" is used in rules: ${ruleNames}`);
      return;
    }

    try {
      const response = await authenticatedFetch(`/api/settlement-variables/${variableId}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (data.success) {
        message.success("Variable deleted");
        fetchVariables();
      } else {
        message.error(data.message || "Failed to delete variable");
      }
    } catch (error) {
      message.error("Failed to delete variable");
    }
  };

  // Open variable modal
  const openVariableModal = (variable = null) => {
    setEditingVariable(variable);
    if (variable) {
      variableForm.setFieldsValue({
        variableName: variable.variable_name,
        displayName: variable.display_name,
        description: variable.description,
        ruleType: variable.rule_type,
        dataSource: variable.data_source,
        tableName: variable.table_name,
        columnName: variable.column_name,
        dataType: variable.data_type,
        isArray: variable.is_array || false,
      });
    } else {
      variableForm.resetFields();
      variableForm.setFieldsValue({
        dataSource: "MYSQL",
        dataType: "NUMBER",
        isArray: false,
      });
    }
    setVariableModalVisible(true);
  };

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      const userRoleType = localStorage.getItem("roleType") || "";
      const isPowowAdmin = ["superadmin", "SUPER", "ADMIN"].includes(userRoleType);

      const promises = [fetchRules(), fetchSummary(), fetchEntities()];
      if (isPowowAdmin) {
        promises.push(fetchVariables());
      }

      await Promise.all(promises);
      setLoading(false);
    };
    loadData();
  }, [fetchRules, fetchSummary, fetchEntities, fetchVariables]);

  // Fetch bindings when type changes
  useEffect(() => {
    if (activeTab === "bindings") {
      fetchBindings(selectedBindingType);
    }
  }, [activeTab, selectedBindingType, fetchBindings]);

  // Create/Update rule
  const handleSaveRule = async (values) => {
    setSaveLoading(true);
    try {
      const isEdit = !!editingRule;

      // Use formulaData from FormulaBuilder or fall back to raw JSON
      const formula = formulaData || (values.formula ? JSON.parse(values.formula) : null);

      // Build params schema from formula constants
      const paramsSchema = formula?.constants ? Object.fromEntries(
        Object.entries(formula.constants).map(([name, config]) => [
          name,
          { type: config.type || "number", default: config.value, min: config.min, max: config.max, description: config.description }
        ])
      ) : null;

      const payload = {
        ruleType: values.ruleType,
        ruleCode: values.ruleCode,
        description: values.description,
        formula: formula,
        paramsSchema: paramsSchema,
        isActive: true, // Always active, removed the switch
      };

      const url = isEdit ? `/api/settlements/rules/${editingRule.rule_id}` : "/api/settlements/rules";
      const method = isEdit ? "PUT" : "POST";

      const response = await authenticatedFetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success) {
        message.success(isEdit ? "Rule updated" : "Rule created");
        setRuleModalVisible(false);
        setEditingRule(null);
        ruleForm.resetFields();
        fetchRules();
        fetchSummary();
      } else {
        message.error(data.message || "Failed to save rule");
      }
    } catch (error) {
      message.error("Failed to save rule");
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete rule
  const handleDeleteRule = async (ruleId) => {
    try {
      const response = await authenticatedFetch(`/api/settlements/rules/${ruleId}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (data.success) {
        message.success("Rule deleted");
        fetchRules();
        fetchSummary();
      } else {
        message.error(data.message || "Failed to delete rule");
      }
    } catch (error) {
      message.error("Failed to delete rule");
    }
  };

  // Create/Update binding
  const handleSaveBinding = async (values) => {
    setSaveLoading(true);
    try {
      const isEdit = !!editingBinding;

      // Check if batch mode (customer binding with array of entityIds)
      const isBatchMode = selectedBindingType === "customer" &&
        customerBindingMode === "batch" &&
        Array.isArray(values.entityId) &&
        !isEdit;

      if (isBatchMode) {
        // Batch create bindings for multiple customers
        const entityIds = values.entityId;
        let successCount = 0;
        let failCount = 0;
        const errors = [];

        for (const entityId of entityIds) {
          const payload = {
            entityId,
            ruleId: values.ruleId,
            effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD"),
            effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
            priority: values.priority,
            isActive: true,
          };

          try {
            const response = await authenticatedFetch(`/api/settlements/bindings/${selectedBindingType}`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify(payload),
            });

            const data = await response.json();
            if (data.success) {
              successCount++;
            } else {
              failCount++;
              errors.push(`${entityId}: ${data.message}`);
            }
          } catch {
            failCount++;
            errors.push(`${entityId}: Network error`);
          }
        }

        if (successCount > 0 && failCount === 0) {
          message.success(`Created ${successCount} binding(s) successfully`);
        } else if (successCount > 0 && failCount > 0) {
          message.warning(`Created ${successCount} binding(s), ${failCount} failed`);
          console.error("Batch binding errors:", errors);
        } else {
          message.error(`Failed to create bindings: ${errors.slice(0, 3).join(", ")}`);
        }

        setBindingModalVisible(false);
        setEditingBinding(null);
        bindingForm.resetFields();
        setCustomerBindingMode("single");
        setBatchFilterValue(null);
        fetchBindings(selectedBindingType);
        fetchSummary();
      } else {
        // Single binding creation/update
        const payload = {
          entityId: values.entityId,
          ruleId: values.ruleId,
          effectiveFrom: values.effectiveFrom?.format("YYYY-MM-DD"),
          effectiveTo: values.effectiveTo?.format("YYYY-MM-DD") || null,
          priority: values.priority,
          isActive: true,
        };

        const url = isEdit
          ? `/api/settlements/bindings/${selectedBindingType}/${editingBinding.binding_id}`
          : `/api/settlements/bindings/${selectedBindingType}`;
        const method = isEdit ? "PUT" : "POST";

        const response = await authenticatedFetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();
        if (data.success) {
          message.success(isEdit ? "Binding updated" : "Binding created");
          setBindingModalVisible(false);
          setEditingBinding(null);
          bindingForm.resetFields();
          fetchBindings(selectedBindingType);
          fetchSummary();
        } else {
          message.error(data.message || "Failed to save binding");
        }
      }
    } catch (error) {
      message.error("Failed to save binding");
    } finally {
      setSaveLoading(false);
    }
  };

  // Delete binding
  const handleDeleteBinding = async (bindingId) => {
    try {
      const response = await authenticatedFetch(`/api/settlements/bindings/${selectedBindingType}/${bindingId}`, {
        method: "DELETE",
      });

      const data = await response.json();
      if (data.success) {
        message.success("Binding deleted");
        fetchBindings(selectedBindingType);
        fetchSummary();
      } else {
        message.error(data.message || "Failed to delete binding");
      }
    } catch (error) {
      message.error("Failed to delete binding");
    }
  };

  // Batch delete bindings
  const handleBatchDeleteBindings = async () => {
    if (selectedBindingKeys.length === 0) return;

    setBatchDeleteLoading(true);
    try {
      const response = await authenticatedFetch(`/api/settlements/bindings/${selectedBindingType}/batch-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ bindingIds: selectedBindingKeys }),
      });

      const data = await response.json();
      if (data.success) {
        message.success(`Deleted ${data.deleted} binding(s)`);
        setSelectedBindingKeys([]);
        fetchBindings(selectedBindingType);
        fetchSummary();
      } else {
        message.error(data.message || "Failed to delete bindings");
      }
    } catch (error) {
      message.error("Failed to delete bindings");
    } finally {
      setBatchDeleteLoading(false);
    }
  };

  // Open rule modal
  const openRuleModal = (rule = null) => {
    setEditingRule(rule);
    if (rule) {
      ruleForm.setFieldsValue({
        ruleType: rule.rule_type,
        ruleCode: rule.rule_code,
        description: rule.description,
      });
      setSelectedRuleType(rule.rule_type);
      setFormulaData(rule.formula || null);
    } else {
      ruleForm.resetFields();
      ruleForm.setFieldsValue({ ruleType: "GROUP_REVENUE" });
      setSelectedRuleType("GROUP_REVENUE");
      setFormulaData(null);
    }
    setRuleModalVisible(true);
  };

  // Open binding modal
  const openBindingModal = (binding = null) => {
    setEditingBinding(binding);
    if (binding) {
      bindingForm.setFieldsValue({
        entityId: binding.entity_id,
        ruleId: binding.rule_id,
        effectiveFrom: binding.effective_from ? dayjs(binding.effective_from) : null,
        effectiveTo: binding.effective_to ? dayjs(binding.effective_to) : null,
        priority: binding.priority,
      });
    } else {
      bindingForm.resetFields();
      bindingForm.setFieldsValue({ priority: 100 });
    }
    setBindingModalVisible(true);
  };

  // Get filtered customers based on batch selection
  const getFilteredCustomers = () => {
    if (customerBindingMode === "single" || !batchFilterValue) {
      return customers;
    }
    if (batchFilterType === "control-group") {
      // Filter customers whose system belongs to the selected control group
      return customers.filter(c => c.control_group_id === batchFilterValue);
    } else if (batchFilterType === "company") {
      // Filter customers belonging to the selected company
      return customers.filter(c => c.company_id === batchFilterValue);
    }
    return customers;
  };

  // Get entity options based on binding type
  const getEntityOptions = () => {
    switch (selectedBindingType) {
      case "control-group":
      case "system":
        return controlGroups.map(g => ({ value: g.id, label: g.name }));
      case "company":
        // Partner Share binding: entity = vg_share row (VG + company + share%).
        // Label encodes all three so user can find the right row.
        return vgShares.map((s) => ({
          value: s.id,
          label: `${s.view_group_name || s.view_group_id} — ${s.company_name || s.company_id} (${Number(s.share_percent).toFixed(2)}%)`,
        }));
      case "customer": {
        const filtered = getFilteredCustomers();
        return filtered.map(c => ({
          value: c.id,
          label: c.name || c.email || c.id,
        }));
      }
      default:
        return [];
    }
  };

  // Filtered rules
  const filteredRules = rules.filter(r => {
    if (ruleFilter === "all") return true;
    return r.rule_type === ruleFilter;
  });

  // Rules table columns
  const ruleColumns = [
    {
      title: "Rule Code",
      dataIndex: "rule_code",
      key: "rule_code",
      render: (code, record) => (
        <div>
          <Text strong>{code}</Text>
          <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
            {record.rule_id}
          </Text>
        </div>
      ),
    },
    {
      title: "Type",
      dataIndex: "rule_type",
      key: "rule_type",
      width: 150,
      render: (type) => {
        const ruleType = RULE_TYPES.find(t => t.value === type);
        return <Tag color={ruleType?.color || "default"}>{ruleType?.label || type}</Tag>;
      },
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc) => desc || <Text type="secondary">—</Text>,
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      width: 80,
      align: "center",
      render: (v) => `v${v}`,
    },
    {
      title: "Bindings",
      key: "bindings",
      width: 180,
      render: (_, record) => {
        const counts = record.bindingCounts || { control_group: 0, system: 0, company: 0, customer: 0, total: 0 };
        if (counts.total === 0) {
          return <Text type="secondary">No bindings</Text>;
        }
        // Show relevant binding count based on rule type
        const parts = [];
        if (record.rule_type === "GROUP_REVENUE" && counts.control_group > 0) {
          parts.push(<Tag key="cg" color="blue">{counts.control_group} group{counts.control_group > 1 ? "s" : ""}</Tag>);
        }
        if (record.rule_type === "SYSTEM_ALLOCATION" && counts.system > 0) {
          parts.push(<Tag key="sys" color="green">{counts.system} system{counts.system > 1 ? "s" : ""}</Tag>);
        }
        if (record.rule_type === "COMPANY_REVENUE" && counts.company > 0) {
          parts.push(<Tag key="co" color="purple">{counts.company} compan{counts.company > 1 ? "ies" : "y"}</Tag>);
        }
        if (record.rule_type === "CUSTOMER_PRICING" && counts.customer > 0) {
          parts.push(<Tag key="cu" color="orange">{counts.customer} customer{counts.customer > 1 ? "s" : ""}</Tag>);
        }
        return parts.length > 0 ? <Space size={4} wrap>{parts}</Space> : <Text type="secondary">No bindings</Text>;
      },
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (active) => (
        <Tag color={active ? "success" : "default"} icon={active ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openRuleModal(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this rule?"
            description="This will fail if rule is bound to entities."
            onConfirm={() => handleDeleteRule(record.rule_id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Bindings table columns.
  // For "company" (Partner Share) binding, the entity is a vg_share row, so the
  // table shows extra columns (VG, share%) so the user can identify the row.
  const bindingColumns = [
    ...(selectedBindingType === "company"
      ? [
          {
            title: "View Group",
            key: "view_group",
            render: (_, record) => (
              <div>
                <Text strong>{record.view_group_name || record.view_group_id}</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                  {record.view_group_id}
                </Text>
              </div>
            ),
          },
          {
            title: "Partner Company",
            key: "company",
            render: (_, record) => (
              <div>
                <Text strong>{record.entity_name || record.company_id}</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                  {record.company_id}
                </Text>
              </div>
            ),
          },
          {
            title: "Share %",
            dataIndex: "share_percent",
            key: "share_percent",
            width: 90,
            align: "right",
            render: (v) => (
              <Text strong style={{ color: token.colorPrimary }}>
                {Number(v).toFixed(2)}%
              </Text>
            ),
          },
        ]
      : [
          {
            title: BINDING_TYPES.find(b => b.key === selectedBindingType)?.label || "Entity",
            key: "entity",
            render: (_, record) => (
              <div>
                <Text strong>{record.entity_name || record.entity_id}</Text>
                <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
                  {record.entity_id}
                </Text>
              </div>
            ),
          },
        ]),
    {
      title: "Rule",
      key: "rule",
      render: (_, record) => {
        // Partner share rows can have a NULL rule_id (= use default formula `sum × % / 100`).
        if (selectedBindingType === "company" && !record.rule_id) {
          return <Text type="secondary">Default (sum × %)</Text>;
        }
        return (
          <div>
            <Text strong>{record.rule_code}</Text>
            <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
              {record.rule_description}
            </Text>
          </div>
        );
      },
    },
    {
      title: "Effective Period",
      key: "period",
      width: 200,
      render: (_, record) => (
        <Text>
          {record.effective_from} - {record.effective_to || "No end date"}
        </Text>
      ),
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: 80,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (active) => (
        <Tag color={active ? "success" : "default"}>
          {active ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openBindingModal(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this binding?"
            onConfirm={() => handleDeleteBinding(record.binding_id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Variables table columns
  const variableColumns = [
    {
      title: "Variable Name",
      dataIndex: "variable_name",
      key: "variable_name",
      render: (name, record) => (
        <div>
          <Text strong code>{name}</Text>
          <Text type="secondary" style={{ display: "block", fontSize: 11 }}>
            {record.display_name}
          </Text>
        </div>
      ),
    },
    {
      title: "Rule Type",
      dataIndex: "rule_type",
      key: "rule_type",
      width: 150,
      render: (type) => {
        const ruleType = RULE_TYPES.find(t => t.value === type);
        return <Tag color={ruleType?.color || "default"}>{ruleType?.label || type}</Tag>;
      },
    },
    {
      title: "Data Source",
      key: "data_source",
      width: 200,
      render: (_, record) => (
        <div>
          <Tag color={record.data_source === "MYSQL" ? "blue" : record.data_source === "CASSANDRA" ? "purple" : record.data_source === "PLATFORM_STATISTICS" ? "cyan" : "orange"}>
            {record.data_source === "MYSQL" ? "PostgreSQL" : record.data_source === "COMPUTED" ? "Computed" : record.data_source}
          </Tag>
          <Text type="secondary" style={{ display: "block", fontSize: 11, marginTop: 4 }}>
            {record.table_name}.{record.column_name}
          </Text>
        </div>
      ),
    },
    {
      title: "Data Type",
      dataIndex: "data_type",
      key: "data_type",
      width: 100,
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: "Interval Data",
      dataIndex: "is_array",
      key: "is_array",
      width: 120,
      render: (isArray) => (
        isArray ? (
          <Tag color="cyan">288 Points</Tag>
        ) : (
          <Text type="secondary">Single Value</Text>
        )
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (desc) => desc || <Text type="secondary">—</Text>,
    },
    {
      title: "Status",
      dataIndex: "is_active",
      key: "is_active",
      width: 100,
      render: (active) => (
        <Tag color={active === "YES" ? "success" : "default"} icon={active === "YES" ? <CheckCircleOutlined /> : <CloseCircleOutlined />}>
          {active === "YES" ? "Active" : "Inactive"}
        </Tag>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit">
            <Button type="text" icon={<EditOutlined />} onClick={() => openVariableModal(record)} />
          </Tooltip>
          <Popconfirm
            title="Delete this variable?"
            description="This will soft-delete the variable (set to inactive)."
            onConfirm={() => handleDeleteVariable(record.variable_id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button type="text" danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // Get filtered variables (only show active ones)
  const filteredVariables = variables.filter(v => {
    if (v.is_active !== 'YES') return false;
    if (variableFilter === "all") return true;
    return v.rule_type === variableFilter;
  });

  // Check if user is Powow Admin or above
  const userRoleType = localStorage.getItem("roleType") || "";
  const isPowowAdmin = ["superadmin", "SUPER", "ADMIN"].includes(userRoleType);

  // Tab items
  const tabItems = [
    {
      key: "rules",
      label: (
        <Space>
          <SettingOutlined />
          Settlement Rules
        </Space>
      ),
      children: (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Space>
              <Select
                value={ruleFilter}
                onChange={setRuleFilter}
                style={{ width: 180 }}
                options={[
                  { value: "all", label: "All Rule Types" },
                  ...RULE_TYPES.map(t => ({ value: t.value, label: t.label })),
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchRules}>
                Refresh
              </Button>
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openRuleModal()}>
              New Rule
            </Button>
          </div>

          <Table
            columns={ruleColumns}
            dataSource={filteredRules}
            rowKey="rule_id"
            pagination={makePagination(rulePageSize, setRulePageSize)}
            loading={loading}
          />
        </div>
      ),
    },
    {
      key: "bindings",
      label: (
        <Space>
          <LinkOutlined />
          Rule Bindings
        </Space>
      ),
      children: (
        <div>
          <div style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              {BINDING_TYPES.map(bt => (
                <Col xs={12} sm={6} key={bt.key}>
                  <Card
                    bordered
                    style={{
                      borderRadius: 8,
                      cursor: "pointer",
                      borderColor: selectedBindingType === bt.key ? token.colorPrimary : token.colorBorder,
                      background: selectedBindingType === bt.key ? token.colorPrimaryBg : undefined,
                    }}
                    onClick={() => { setSelectedBindingType(bt.key); setSelectedBindingKeys([]); }}
                    size="small"
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 18, color: selectedBindingType === bt.key ? token.colorPrimary : token.colorTextSecondary }}>
                        {bt.icon}
                      </span>
                      <div>
                        <Text strong>{bt.label}</Text>
                        <Text type="secondary" style={{ display: "block", fontSize: 11 }}>{bt.description}</Text>
                      </div>
                    </div>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={() => fetchBindings(selectedBindingType)}>
                Refresh
              </Button>
              {selectedBindingKeys.length > 0 && (
                <Popconfirm
                  title={`Delete ${selectedBindingKeys.length} selected binding(s)?`}
                  description="This action cannot be undone."
                  onConfirm={handleBatchDeleteBindings}
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: batchDeleteLoading }}
                >
                  <Button danger icon={<DeleteOutlined />} loading={batchDeleteLoading}>
                    Delete Selected ({selectedBindingKeys.length})
                  </Button>
                </Popconfirm>
              )}
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openBindingModal()}>
              New Binding
            </Button>
          </div>

          <Table
            columns={bindingColumns}
            dataSource={bindings}
            rowKey="binding_id"
            rowSelection={{
              selectedRowKeys: selectedBindingKeys,
              onChange: setSelectedBindingKeys,
              preserveSelectedRowKeys: true,
              selections: [
                Table.SELECTION_ALL,
                Table.SELECTION_INVERT,
                Table.SELECTION_NONE,
              ],
            }}
            pagination={makePagination(bindingPageSize, setBindingPageSize)}
            locale={{ emptyText: <Empty description={`No ${selectedBindingType} bindings found`} /> }}
          />
        </div>
      ),
    },
  ];

  // Add Variable Management tab only for Powow Admin and above
  if (isPowowAdmin) {
    tabItems.push({
      key: "variables",
      label: (
        <Space>
          <DatabaseOutlined />
          Variable Management
          <Tag color="purple" style={{ marginLeft: 4 }}>Powow Admin</Tag>
        </Space>
      ),
      children: (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Variable Management"
            description="Variables define the mapping between formula variable names and actual database tables/columns. This prevents table/column mapping issues in settlement rules."
          />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <Space>
              <Select
                value={variableFilter}
                onChange={setVariableFilter}
                style={{ width: 180 }}
                options={[
                  { value: "all", label: "All Rule Types" },
                  ...RULE_TYPES.map(t => ({ value: t.value, label: t.label })),
                ]}
              />
              <Button icon={<ReloadOutlined />} onClick={fetchVariables}>
                Refresh
              </Button>
            </Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openVariableModal()}>
              New Variable
            </Button>
          </div>

          <Table
            columns={variableColumns}
            dataSource={filteredVariables}
            rowKey="variable_id"
            pagination={makePagination(variablePageSize, setVariablePageSize)}
            loading={variableLoading}
          />
        </div>
      ),
    });
  }

  if (loading && rules.length === 0) {
    return (
      <PageLayout active="settlementRules">
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 400 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="settlementRules">
      {/* Page Header */}
      <div style={{ marginBottom: 24 }}>
        <Title level={2} style={{ marginBottom: 4 }}>
          Settlement Rule Management
        </Title>
        <Text type="secondary" style={{ fontSize: 14 }}>
          Configure settlement rules and bind them to control groups, systems, companies, and customers
        </Text>
      </div>

      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={8} md={4}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic title="Active Rules" value={summary.rules || 0} valueStyle={{ color: token.colorPrimary }} />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Control Group Bindings"
              value={summary.controlGroupBindings || 0}
              prefix={<TeamOutlined style={{ marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="System Bindings"
              value={summary.systemBindings || 0}
              prefix={<DesktopOutlined style={{ marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="View Group Bindings"
              value={summary.viewGroupBindings || 0}
              prefix={<AppstoreOutlined style={{ marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Company Bindings"
              value={summary.companyBindings || 0}
              prefix={<BankOutlined style={{ marginRight: 4 }} />}
            />
          </Card>
        </Col>
        <Col xs={12} sm={8} md={4}>
          <Card bordered={false} style={{ borderRadius: 12 }}>
            <Statistic
              title="Customer Bindings"
              value={summary.customerBindings || 0}
              prefix={<UserOutlined style={{ marginRight: 4 }} />}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card bordered={false} style={{ borderRadius: 12 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabItems} />
      </Card>

      {/* Rule Modal */}
      <Modal
        title={editingRule ? "Edit Settlement Rule" : "Create Settlement Rule"}
        open={ruleModalVisible}
        onCancel={() => {
          setRuleModalVisible(false);
          setEditingRule(null);
          setFormulaData(null);
          ruleForm.resetFields();
        }}
        footer={null}
        width={900}
        destroyOnClose
      >
        <Form form={ruleForm} layout="vertical" onFinish={handleSaveRule}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="ruleType"
                label="Rule Type"
                rules={[{ required: true, message: "Rule type is required" }]}
              >
                <Select
                  options={RULE_TYPES}
                  disabled={!!editingRule}
                  onChange={(value) => {
                    setSelectedRuleType(value);
                    setFormulaData(null); // Reset formula when type changes
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="ruleCode"
                label="Rule Code"
                rules={[{ required: true, message: "Rule code is required" }]}
              >
                <Input placeholder="e.g., FCAS_AVAIL_POWER" disabled={!!editingRule} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="description" label="Description">
            <TextArea rows={2} placeholder="Describe what this rule calculates" />
          </Form.Item>

          <Divider>Formula Builder</Divider>

          <FormulaBuilder
            ruleType={selectedRuleType}
            value={formulaData}
            onChange={setFormulaData}
            variables={variables}
          />

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Space>
              <Button onClick={() => setRuleModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                {editingRule ? "Update" : "Create"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Variable Modal */}
      <Modal
        title={editingVariable ? "Edit Variable" : "Create Variable"}
        open={variableModalVisible}
        onCancel={() => {
          setVariableModalVisible(false);
          setEditingVariable(null);
          variableForm.resetFields();
        }}
        footer={null}
        width={700}
        destroyOnClose
      >
        <Form form={variableForm} layout="vertical" onFinish={handleSaveVariable}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="variableName"
                label="Variable Name"
                rules={[{ required: true, message: "Variable name is required" }]}
                tooltip="Used in formulas (e.g., aemo_energy_price)"
              >
                <Input placeholder="e.g., aemo_energy_price" disabled={!!editingVariable} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="Display Name"
                rules={[{ required: true, message: "Display name is required" }]}
              >
                <Input placeholder="e.g., AEMO Energy Price" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="Description"
          >
            <TextArea rows={2} placeholder="Describe what this variable represents" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="ruleType"
                label="Rule Type"
                rules={[{ required: true, message: "Rule type is required" }]}
                tooltip="Which rule types can use this variable"
              >
                <Select options={RULE_TYPES} placeholder="Select rule type" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="dataSource"
                label="Data Source"
                rules={[{ required: true, message: "Data source is required" }]}
              >
                <Select
                  options={Object.entries(DATA_SOURCE_CONFIG).map(([key, config]) => ({
                    value: key,
                    label: config.label,
                  }))}
                  placeholder="Select data source"
                  onChange={() => {
                    variableForm.setFieldsValue({ tableName: undefined, columnName: undefined });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.dataSource !== curr.dataSource}
              >
                {({ getFieldValue }) => {
                  const dataSource = getFieldValue("dataSource");
                  const tables = dataSource ? DATA_SOURCE_CONFIG[dataSource]?.tables || {} : {};
                  return (
                    <Form.Item
                      name="tableName"
                      label="Table Name"
                      rules={[{ required: true, message: "Table name is required" }]}
                    >
                      <Select
                        placeholder="Select table"
                        disabled={!dataSource}
                        options={Object.entries(tables).map(([key, tableConfig]) => ({
                          value: key,
                          label: tableConfig.label,
                        }))}
                        onChange={() => {
                          variableForm.setFieldsValue({ columnName: undefined });
                        }}
                        showSearch
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                noStyle
                shouldUpdate={(prev, curr) => prev.dataSource !== curr.dataSource || prev.tableName !== curr.tableName}
              >
                {({ getFieldValue }) => {
                  const dataSource = getFieldValue("dataSource");
                  const tableName = getFieldValue("tableName");
                  const columns = dataSource && tableName
                    ? DATA_SOURCE_CONFIG[dataSource]?.tables?.[tableName]?.columns || []
                    : [];
                  return (
                    <Form.Item
                      name="columnName"
                      label="Column Name"
                      rules={[{ required: true, message: "Column name is required" }]}
                    >
                      <Select
                        placeholder="Select column"
                        disabled={!tableName}
                        options={columns.map((col) => ({
                          value: col.value,
                          label: col.label + (col.isArray ? " [288]" : ""),
                        }))}
                        onChange={(value) => {
                          const selectedCol = columns.find(c => c.value === value);
                          if (selectedCol) {
                            variableForm.setFieldsValue({ isArray: selectedCol.isArray || false });
                          }
                        }}
                        showSearch
                        optionFilterProp="label"
                      />
                    </Form.Item>
                  );
                }}
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="dataType"
                label="Data Type"
                rules={[{ required: true, message: "Data type is required" }]}
              >
                <Select
                  options={[
                    { value: "NUMBER", label: "Number" },
                    { value: "STRING", label: "String" },
                    { value: "DATE", label: "Date" },
                    { value: "BOOLEAN", label: "Boolean" },
                  ]}
                  placeholder="Select data type"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="isArray"
                label="288-Point Interval Data"
                valuePropName="checked"
                tooltip="Automatically set based on selected column. Check if this variable contains 288-point interval data."
              >
                <Switch
                  checkedChildren="Yes"
                  unCheckedChildren="No"
                  style={{ height: 22, minHeight: 22 }}
                />
              </Form.Item>
            </Col>
          </Row>

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Space>
              <Button onClick={() => setVariableModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                {editingVariable ? "Update" : "Create"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>

      {/* Binding Modal */}
      <Modal
        title={editingBinding ? "Edit Rule Binding" : "Create Rule Binding"}
        open={bindingModalVisible}
        onCancel={() => {
          setBindingModalVisible(false);
          setEditingBinding(null);
          bindingForm.resetFields();
          setCustomerBindingMode("single");
          setBatchFilterValue(null);
        }}
        footer={null}
        width={600}
        destroyOnClose
      >
        <Form form={bindingForm} layout="vertical" onFinish={handleSaveBinding}>
          {/* Customer batch mode selection */}
          {selectedBindingType === "customer" && !editingBinding && (
            <>
              <Form.Item label="Binding Mode">
                <Radio.Group
                  value={customerBindingMode}
                  onChange={(e) => {
                    setCustomerBindingMode(e.target.value);
                    setBatchFilterValue(null);
                    bindingForm.setFieldValue("entityId", undefined);
                  }}
                >
                  <Radio.Button value="single">Single Customer</Radio.Button>
                  <Radio.Button value="batch">Batch by Group</Radio.Button>
                </Radio.Group>
              </Form.Item>

              {customerBindingMode === "batch" && (
                <Row gutter={16}>
                  <Col span={8}>
                    <Form.Item label="Filter By">
                      <Select
                        value={batchFilterType}
                        onChange={(v) => {
                          setBatchFilterType(v);
                          setBatchFilterValue(null);
                          bindingForm.setFieldValue("entityId", undefined);
                        }}
                        options={[
                          { value: "control-group", label: "Control Group" },
                          { value: "company", label: "Company" },
                        ]}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={16}>
                    <Form.Item label={batchFilterType === "control-group" ? "Control Group" : "Company"}>
                      <Select
                        showSearch
                        placeholder={`Select ${batchFilterType === "control-group" ? "control group" : "company"}`}
                        value={batchFilterValue}
                        onChange={(v) => {
                          setBatchFilterValue(v);
                          bindingForm.setFieldValue("entityId", undefined);
                        }}
                        options={
                          batchFilterType === "control-group"
                            ? controlGroups.map(g => ({ value: g.id, label: g.name }))
                            : companies.map(c => ({ value: c.id, label: c.name || c.company_name }))
                        }
                        filterOption={(input, option) =>
                          (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                        }
                      />
                    </Form.Item>
                  </Col>
                </Row>
              )}

              {customerBindingMode === "batch" && batchFilterValue && (
                <Alert
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                  message={`${getFilteredCustomers().length} customer(s) found`}
                  description="Select customers below or use 'Select All' to bind the rule to all filtered customers."
                />
              )}
            </>
          )}

          <Form.Item
            name="entityId"
            label={`${BINDING_TYPES.find(b => b.key === selectedBindingType)?.label || "Entity"}${
              selectedBindingType === "customer" && customerBindingMode === "batch" ? " (multi-select)" : ""
            }`}
            rules={[{ required: true, message: "Entity is required" }]}
          >
            <Select
              showSearch
              mode={selectedBindingType === "customer" && customerBindingMode === "batch" ? "multiple" : undefined}
              placeholder={selectedBindingType === "customer" && customerBindingMode === "batch"
                ? "Select customers (or click 'Select All')"
                : "Select entity"}
              options={getEntityOptions()}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              disabled={!!editingBinding}
              maxTagCount={5}
              maxTagPlaceholder={(omitted) => `+${omitted.length} more`}
            />
          </Form.Item>

          {/* Select All button for batch mode */}
          {selectedBindingType === "customer" && customerBindingMode === "batch" && !editingBinding && (
            <div style={{ marginBottom: 16, marginTop: -8 }}>
              <Button
                size="small"
                onClick={() => {
                  const allIds = getFilteredCustomers().map(c => c.id);
                  bindingForm.setFieldValue("entityId", allIds);
                }}
              >
                Select All ({getFilteredCustomers().length})
              </Button>
              <Button
                size="small"
                style={{ marginLeft: 8 }}
                onClick={() => bindingForm.setFieldValue("entityId", [])}
              >
                Clear All
              </Button>
            </div>
          )}

          <Form.Item
            name="ruleId"
            label="Settlement Rule"
            rules={[{ required: true, message: "Rule is required" }]}
          >
            <Select
              showSearch
              placeholder="Select rule"
              options={rules
                .filter(r => {
                  if (!r.is_active) return false;
                  // Filter by rule type based on binding type
                  const ruleTypeMap = {
                    "control-group": "GROUP_REVENUE",
                    "system": "SYSTEM_ALLOCATION",
                    "company": "COMPANY_REVENUE",
                    "customer": "CUSTOMER_PRICING",
                  };
                  const expectedRuleType = ruleTypeMap[selectedBindingType];
                  return expectedRuleType ? r.rule_type === expectedRuleType : true;
                })
                .map(r => ({
                  value: r.rule_id,
                  label: `${r.rule_code} (${r.rule_type})`,
                }))}
              filterOption={(input, option) =>
                (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
              }
              disabled={!!editingBinding}
            />
          </Form.Item>

          {/* Partner Share bindings inherit effective period + priority from the vg_share row,
              so those fields are configured in Partner Revenue Shares, not here. */}
          {selectedBindingType !== "company" && (
            <>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="effectiveFrom"
                    label="Effective From"
                    rules={[{ required: true, message: "Start date is required" }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="effectiveTo" label="Effective To">
                    <DatePicker style={{ width: "100%" }} placeholder="No end date" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="priority" label="Priority" tooltip="Lower number = higher priority">
                <InputNumber min={1} max={1000} style={{ width: "100%" }} />
              </Form.Item>
            </>
          )}

          {selectedBindingType === "company" && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Effective period, share % and priority are configured on the vg_share row itself (see Companies → Partner Revenue Shares). This screen only attaches an algorithm to that row."
            />
          )}

          <div style={{ textAlign: "right", marginTop: 16 }}>
            <Space>
              <Button onClick={() => setBindingModalVisible(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saveLoading}>
                {editingBinding ? "Update" : "Create"}
              </Button>
            </Space>
          </div>
        </Form>
      </Modal>
    </PageLayout>
  );
}
